import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { useScope } from "../../lib/me";
import { AppLink } from "../board/cells";
import { useAction } from "../lib/actions";
import { entityHref, entityLabel } from "../lib/entity";
import { fmtPct, fmtWhen } from "../lib/time";
import { ActionError, Empty, QueryView, Section, TableScroll } from "../lib/ui";
import { intParam, type Patch, useUrlState } from "../lib/url";
import { DiagnosticsPanel } from "./Diagnostics";
import { SourceHealthTable, useSourcesHealth } from "./SourceHealth";

type Job = Schemas["JobOut"];

export const JOB_STATUSES = ["queued", "running", "done", "failed", "cancelled"] as const;
export const POOLS = ["net", "cpu", "heavy", "render", "browser"] as const;

export type JobParams = { status?: string; pool?: string; kind?: string; job?: number };

export function parseJobParams(sp: URLSearchParams): JobParams {
  return { status: sp.get("status") || undefined, pool: sp.get("pool") || undefined, kind: sp.get("kind") || undefined,
    job: intParam(sp.get("job")) };
}

export function jobParamsToPatch(p: JobParams): Patch {
  return { status: p.status, pool: p.pool, kind: p.kind, job: p.job };
}

const ACTIVE = new Set(["queued", "running"]);
const lastLine = (s: string | null | undefined) => (s ? (s.trim().split("\n").at(-1) ?? "") : "");

function useJobActions(onRetried: (job: Job) => void) {
  const invalidate = [["jobs"]];
  const retry = useAction((id: number) => unwrap(api.POST("/api/jobs/{job_id}/retry", { params: { path: { job_id: id } } })), {
    label: (id) => `Retry job #${id}`,
    success: (j, id) => `Retried job #${id} as job #${j.id} (${j.status})`,
    invalidate,
  });
  const cancel = useAction((id: number) => unwrap(api.POST("/api/jobs/{job_id}/cancel", { params: { path: { job_id: id } } })), {
    label: (id) => `Cancel job #${id}`,
    success: (j) => `Cancelled job #${j.id}`,
    invalidate,
  });
  return {
    retry: async (id: number) => {
      const j = await retry.run(id);
      if (j) onRetried(j);
    },
    cancel: (id: number) => void cancel.run(id),
    pending: retry.pending || cancel.pending,
    error: retry.error ?? cancel.error,
  };
}

function JobButtons({ job, actions }: { job: Job; actions: ReturnType<typeof useJobActions> }) {
  return (
    <>
      {ACTIVE.has(job.status) && (
        <button type="button" className="ws7-danger" data-testid={`job-cancel-${job.id}`} disabled={actions.pending}
          aria-label={`Cancel job #${job.id}`} onClick={() => actions.cancel(job.id)}>
          Cancel
        </button>
      )}
      {(job.status === "failed" || job.status === "cancelled") && (
        <button type="button" className="ws7-primary" data-testid={`job-retry-${job.id}`} disabled={actions.pending}
          aria-label={`Retry job #${job.id}`} onClick={() => void actions.retry(job.id)}>
          Retry
        </button>
      )}
    </>
  );
}

function JobStatus({ job }: { job: Job }) {
  const label = job.status === "queued" && job.queue_position != null ? `#${job.queue_position}` : undefined;
  return <StatusPill state={job.status} label={label} />;
}

function PoolSummary() {
  const q = useQuery({ queryKey: qk.jobs({ summary: true }), queryFn: () => unwrap(api.GET("/api/jobs/summary")), refetchInterval: 10_000 });
  return (
    <QueryView q={q} what="pools">
      {(d) => (
        <TableScroll label="Pool summary table">
          <table className="ws7-table" aria-label="Pools" data-testid="pools">
            <thead>
              <tr>
                <th scope="col">Pool</th>
                <th scope="col" className="num">Slots</th>
                <th scope="col" className="num">Running</th>
                <th scope="col" className="num">Queued</th>
                <th scope="col" className="num">Failed 24 h</th>
                <th scope="col" className="num">Done 24 h</th>
                <th scope="col">Oldest queued</th>
              </tr>
            </thead>
            <tbody>
              {d.pools.map((p) => (
                <tr key={p.pool} data-testid={`pool-${p.pool}`}>
                  <th scope="row">{p.pool}</th>
                  <td className="num">{p.slots}</td>
                  <td className="num">{p.running}</td>
                  <td className="num">{p.queued}</td>
                  <td className={`num${p.failed_24h ? " ws7-tone-bad" : ""}`}>{p.failed_24h}</td>
                  <td className="num">{p.done_24h}</td>
                  <td>{fmtWhen(p.oldest_queued_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </QueryView>
  );
}

function JobDetail({ id, actions }: { id: number; actions: ReturnType<typeof useJobActions> }) {
  const qc = useQueryClient();
  const announce = useAnnounce();
  const job = useQuery({
    queryKey: qk.jobs({ id }),
    queryFn: () => unwrap(api.GET("/api/jobs/{job_id}", { params: { path: { job_id: id } } })),
    refetchInterval: (q) => (q.state.data && ACTIVE.has(q.state.data.status) ? 2000 : false),
  });
  const running = !!job.data && ACTIVE.has(job.data.status);
  const log = useQuery({
    queryKey: qk.jobs({ id, log: true }),
    queryFn: () => unwrap(api.GET("/api/jobs/{job_id}/log", { params: { path: { job_id: id }, query: { tail: 20000 } }, parseAs: "text" })),
    enabled: !!job.data?.has_log,
    refetchInterval: running ? 2000 : false,
  });
  return (
    <Section id="job-detail" title={`Job #${id}`}
      actions={
        <>
          {job.data && <JobButtons job={job.data} actions={actions} />}
          <button type="button" data-testid="job-refresh" onClick={async () => {
            await qc.invalidateQueries({ queryKey: qk.jobs({ id }) });
            await qc.invalidateQueries({ queryKey: qk.jobs({ id, log: true }) });
            announce(`Refreshed job #${id}`);
          }}>
            Refresh
          </button>
        </>
      }>
      <QueryView q={job} what={`job #${id}`}>
        {(j) => (
          <>
            <dl className="ws7-kv" data-testid="job-fields">
              <dt>Kind</dt><dd className="ws7-mono">{j.kind}</dd>
              <dt>Status</dt><dd><JobStatus job={j} />{running && <span className="ws7-muted"> (auto-refreshing)</span>}</dd>
              <dt>Pool</dt><dd>{j.pool}</dd>
              <dt>Entity</dt>
              <dd>{entityHref(j.entity, j.entity_id) ? <AppLink href={entityHref(j.entity, j.entity_id) ?? ""}>{entityLabel(j.entity, j.entity_id)}</AppLink> : entityLabel(j.entity, j.entity_id) || "–"}</dd>
              <dt>Step</dt><dd>{j.step ?? "–"}</dd>
              <dt>Attempts</dt><dd>{j.attempts}/{j.max_attempts}</dd>
              <dt>Priority</dt><dd>{j.priority}</dd>
              <dt>Progress</dt><dd>{j.progress != null ? fmtPct(j.progress) : "–"} {j.message ?? ""}</dd>
              <dt>Requested by</dt><dd>{j.requested_by}</dd>
              <dt>Worker</dt><dd>{j.worker ?? "–"}</dd>
              <dt>Created</dt><dd>{fmtWhen(j.created_at)}</dd>
              <dt>Started</dt><dd>{fmtWhen(j.started_at) || "–"}</dd>
              <dt>Finished</dt><dd>{fmtWhen(j.finished_at) || "–"}</dd>
            </dl>
            {j.error && (
              <>
                <h3>Error</h3>
                <pre className="ws7-pre ws7-tone-bad" data-testid="job-error" tabIndex={0}>{j.error}</pre>
              </>
            )}
            <h3>Payload</h3>
            <pre className="ws7-pre" tabIndex={0}>{JSON.stringify(j.payload, null, 2)}</pre>
            {j.result != null && (
              <>
                <h3>Result</h3>
                <pre className="ws7-pre" tabIndex={0}>{JSON.stringify(j.result, null, 2)}</pre>
              </>
            )}
            <h3>Log tail</h3>
            {!j.has_log ? (
              <Empty>No log yet.</Empty>
            ) : (
              <QueryView q={log} what="log">
                {(text) => <pre className="ws7-pre" data-testid="job-log" tabIndex={0} aria-label={`Log of job #${id}`}>{text || "(empty)"}</pre>}
              </QueryView>
            )}
          </>
        )}
      </QueryView>
    </Section>
  );
}

export function JobsView() {
  const announce = useAnnounce();
  const isAdmin = useScope("admin");
  const { sp, set } = useUrlState();
  const p = parseJobParams(sp);
  const listQuery = { status: p.status, pool: p.pool, kind: p.kind, limit: 200 };
  const jobs = useQuery({
    queryKey: qk.jobs(listQuery),
    queryFn: () => unwrap(api.GET("/api/jobs", { params: { query: listQuery } })),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
  const kinds = useSourcesHealth();
  const actions = useJobActions((j) => set({ job: j.id }));
  const filter = (patch: Patch, msg: string) => {
    set(patch);
    announce(msg);
  };
  return (
    <div className="ws7-page" data-testid="jobs-page">
      <PageHeader title="Jobs" md={{ page: "jobs", params: { status: p.status, pool: p.pool, kind: p.kind } }} />
      <Section id="pools" title="Pools">
        <PoolSummary />
      </Section>
      <Section id="diagnostics" title="Diagnostics">
        <DiagnosticsPanel />
      </Section>
      <div className="ws7-jobs-layout">
        <Section id="job-list" title="Job list">
          <form className="ws7-toolbar" aria-label="Job filters" onSubmit={(e) => e.preventDefault()}>
            <label className="ws7-field">
              Status
              <select value={p.status ?? ""} data-testid="jobs-status" onChange={(e) => filter({ status: e.target.value || undefined }, `Filter: status ${e.target.value || "any"}`)}>
                <option value="">Any status</option>
                {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="ws7-field">
              Pool
              <select value={p.pool ?? ""} data-testid="jobs-pool" onChange={(e) => filter({ pool: e.target.value || undefined }, `Filter: pool ${e.target.value || "any"}`)}>
                <option value="">Any pool</option>
                {POOLS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="ws7-field">
              Kind
              <select value={p.kind ?? ""} data-testid="jobs-kind" onChange={(e) => filter({ kind: e.target.value || undefined }, `Filter: kind ${e.target.value || "any"}`)}>
                <option value="">Any kind</option>
                {(kinds.data ?? []).map((k) => <option key={k.kind} value={k.kind}>{k.kind}</option>)}
                {p.kind && !(kinds.data ?? []).some((k) => k.kind === p.kind) && <option value={p.kind}>{p.kind}</option>}
              </select>
            </label>
            <button type="button" data-testid="jobs-clear" onClick={() => filter({ status: undefined, pool: undefined, kind: undefined }, "Job filters cleared")}>
              Clear filters
            </button>
          </form>
          <ActionError error={actions.error} />
          <QueryView q={jobs} what="jobs">
            {(rows) =>
              rows.length === 0 ? (
                <Empty>No jobs match these filters.</Empty>
              ) : (
                <TableScroll label="Job table">
                  <table className="ws7-table" aria-label="Jobs" data-testid="jobs-table">
                    <thead>
                      <tr>
                        <th scope="col" className="num">#</th>
                        <th scope="col">Kind</th>
                        <th scope="col">Pool</th>
                        <th scope="col">Entity</th>
                        <th scope="col">Status</th>
                        <th scope="col">Attempts</th>
                        <th scope="col">Created</th>
                        <th scope="col">Message / error</th>
                        <th scope="col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((j) => {
                        const href = entityHref(j.entity, j.entity_id);
                        return (
                          <tr key={j.id} data-testid={`job-row-${j.id}`} className={p.job === j.id ? "ws7-selected" : undefined}>
                            <td className="num">{j.id}</td>
                            <td className="ws7-mono">{j.kind}</td>
                            <td>{j.pool}</td>
                            <td>{href ? <AppLink href={href}>{entityLabel(j.entity, j.entity_id)}</AppLink> : entityLabel(j.entity, j.entity_id)}</td>
                            <td><JobStatus job={j} />{j.status === "running" && j.progress != null && <span> {fmtPct(j.progress)}</span>}</td>
                            <td>{j.attempts}/{j.max_attempts}</td>
                            <td>{fmtWhen(j.created_at)}</td>
                            <td className={j.error ? "ws7-tone-bad" : undefined}>{lastLine(j.error ?? j.message)}</td>
                            <td>
                              <span className="ws7-inline">
                                <button type="button" data-testid={`job-details-${j.id}`} aria-label={`Details of job #${j.id}`}
                                  aria-pressed={p.job === j.id}
                                  onClick={() => filter({ job: j.id }, `Showing job #${j.id}`)}>
                                  Details
                                </button>
                                <JobButtons job={j} actions={actions} />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableScroll>
              )
            }
          </QueryView>
        </Section>
        {p.job != null ? (
          <JobDetail key={p.job} id={p.job} actions={actions} />
        ) : (
          <Section id="job-detail" title="Job detail">
            <Empty>Choose “Details” on a job to see its fields and log.</Empty>
          </Section>
        )}
      </div>
      <Section id="sources" title="Source health and kill switches">
        <SourceHealthTable canSwitch={isAdmin} />
      </Section>
    </div>
  );
}
